import React from "react";
import { MoreHorizontal } from "lucide-react";

import TeamSlot from "./TeamSlot";
import InlineEditableName from "./InlineEditableName";

const GroupCard = ({
  group,
  phaseGroup,
  openDropdownPhaseId,
  setOpenDropdownPhaseId,
  onEdit,
  onDelete,
  onExtraPoints,
  onAutoAssign,
  // Team slot shared props
  teamSlotProps,
}) => {
  const dropdownId = `group-${group._id}`;

  return (
    <div
      className="flex flex-col items-start gap-[12px] px-[16px] py-[12px] border border-[#192634] rounded-[20px] bg-[#011224] w-[245px]"
    >
      {/* Group header */}
      <div className="w-full flex justify-between items-center">
        <InlineEditableName
          name={group.name || "Group"}
          onRename={(newName) => teamSlotProps.handleRenameBlock?.(phaseGroup.id, group._id, newName)}
          canEdit={!!teamSlotProps.canEditFormat}
        />
        <div className="relative">
          <button
            className="bg-transparent border-none p-1 cursor-pointer text-app-text-secondary hover:bg-app-border rounded-full transition-all flex items-center justify-center"
            onClick={() => {
              setOpenDropdownPhaseId(
                openDropdownPhaseId === dropdownId ? null : dropdownId
              );
            }}
          >
            <MoreHorizontal />
          </button>

          {openDropdownPhaseId === dropdownId && (
            <div className="absolute top-full right-0 mt-2 w-[200px] bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl overflow-hidden z-[1000]">
              <button
                className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                onClick={() => {
                  setOpenDropdownPhaseId(null);
                  onEdit(phaseGroup, group);
                }}
              >
                Edit
              </button>
              <button
                className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                onClick={() => {
                  setOpenDropdownPhaseId(null);
                  onAutoAssign(phaseGroup.id, group);
                }}
              >
                Auto assign teams
              </button>
              <button
                className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                onClick={() => {
                  setOpenDropdownPhaseId(null);
                  onExtraPoints(phaseGroup.id, group);
                }}
              >
                Extra points
              </button>
              <button
                className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-red-400 hover:bg-red-500/20 hover:text-red-300 cursor-pointer transition-colors"
                onClick={() => {
                  setOpenDropdownPhaseId(null);
                  onDelete(phaseGroup, group);
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Team slots */}
      <div className="w-full flex flex-col gap-[12px]">
        {(group.teams || []).map((team, index) => {
          const ruleText = team.isDummy
            ? teamSlotProps.getProgressionRuleText(group._id, team.dummySlotNumber)
            : null;
          const teamData = team.teamId
            ? teamSlotProps.categoryTeams?.find(t => t._id === team.teamId?._id || t._id === team.teamId)
            : null;
          const teamCrest = teamData?.teamId?.crest || team.teamId?.crest;
          return (
            <TeamSlot
              key={`team-${group._id}-${index}`}
              team={team}
              teamName={team.teamId ? (teamSlotProps.getTeamById(team.teamId) || "Unknown Team") : null}
              teamCrest={teamCrest}
              placeholderText={ruleText || "Choose the team"}
              blockId={group._id}
              phaseId={phaseGroup.id}
              slotIndex={index}
              assignSlot={index + 1}
              isSelectorOpen={teamSlotProps.showTeamSelector && teamSlotProps.selectedBlockId === group._id && teamSlotProps.selectedTeamSlot === index}
              teamSelectorRef={teamSlotProps.teamSelectorRef}
              dropDirection={"down"}
              progressionRuleText={ruleText}
              availableTeams={teamSlotProps.getAvailableTeams()}
              onOpenSelector={() => teamSlotProps.openTeamSelector(phaseGroup.id, group._id, index)}
              onAssignTeam={teamSlotProps.handleAssignTeam}
              onUnassignTeam={teamSlotProps.handleUnassignTeam}
              onRemoveRule={() => teamSlotProps.handleRemoveRule(group._id, team.dummySlotNumber)}
              hasExplicitRule={!!teamSlotProps.findRuleForSlot(group._id, team.dummySlotNumber)}
              ruleSources={teamSlotProps.getRuleSources?.(phaseGroup.id, group._id) || []}
              onAssignRule={(source) => teamSlotProps.handleAssignRuleFromClick?.(source, phaseGroup.id, group._id, index + 1)}
              DroppableSlot={teamSlotProps.DroppableSlot}
              DraggablePosition={teamSlotProps.DraggablePosition}
              onDrop={teamSlotProps.handleCreateRuleFromDrag}
              phases={teamSlotProps.phases}
            />
          );
        })}
      </div>
    </div>
  );
};

export default GroupCard;
